<template>
  <div id="gap-backtest-corporate-events">
    <div class="container-fluid pt-4 px-4">
      <v-alert type="info" variant="tonal" density="comfortable" class="mb-4" rounded="lg">
        Upcoming dividend/bonus/split/buyback ex-dates for the current stock universe, sourced from NSE's own
        Corporate Actions feed. <strong>Data layer only</strong> -- nothing here feeds GAP_FADE or SECTOR_MOMENTUM
        yet; see <router-link to="/gap-backtest/universe">Stock Universe</router-link> to onboard stocks.
      </v-alert>

      <v-card flat rounded="lg" class="gap-backtest-card">
        <v-card-title class="d-flex justify-space-between align-center flex-wrap ga-3 px-5 py-4">
          <span class="text-h6 font-weight-medium">Corporate Events</span>
          <div class="d-flex align-center ga-3">
            <v-switch
              v-model="showInactive"
              label="Show inactive"
              density="compact"
              hide-details
              color="primary"
            />
            <span class="text-caption text-medium-emphasis">{{ filteredEvents.length }} upcoming</span>
            <v-btn variant="tonal" color="primary" size="small" :loading="refreshing" @click="refreshFromNse">
              Refresh from NSE
            </v-btn>
            <v-btn variant="text" size="small" :loading="loading" @click="loadEvents">
              <font-awesome-icon icon="fa-solid fa-rotate-right" class="mr-1" /> Reload
            </v-btn>
          </div>
        </v-card-title>
        <div v-if="refreshSummary" class="px-5 pb-3 text-caption text-medium-emphasis">
          Last refresh: {{ refreshSummary.matchedToNiftyUniverse }} matched, {{ refreshSummary.inserted }} newly stored
          ({{ refreshSummary.alreadyPresent }} already had it, {{ refreshSummary.notOnboarded }} not onboarded yet).
        </div>
        <div v-if="refreshError" class="px-5 pb-3 text-caption text-error">{{ refreshError }}</div>
        <v-data-table
          :headers="headers"
          :items="filteredEvents"
          item-value="key"
          fixed-header
          :items-per-page="-1"
          hide-default-footer
          class="custom-data-table"
        >
          <template v-slot:[`item.eventType`]="{ item }">
            <v-chip size="small" variant="tonal" :color="eventTypeColor(item.eventType)">{{ capitalize(item.eventType) }}</v-chip>
          </template>
          <template v-slot:[`item.eventDate`]="{ item }">
            {{ $helper.formatDate(item.eventDate, false, true) }}
          </template>
          <template v-slot:no-data>
            <div class="py-6 text-center text-medium-emphasis">No upcoming corporate events stored yet.</div>
          </template>
        </v-data-table>
      </v-card>
    </div>
  </div>
</template>

<script>
import { fetchWrapper } from '@/helpers/fetch-wrapper';

export default {
  name: 'GapBacktestCorporateEvents-page',
  data() {
    return {
      events: [],
      loading: false,
      showInactive: false,
      refreshing: false,
      refreshSummary: null,
      refreshError: null,
      headers: [
        { title: 'Code', key: 'code' },
        { title: 'Event', key: 'eventType' },
        { title: 'Date', key: 'eventDate' },
        { title: 'Purpose', key: 'rawPurpose' },
      ],
    };
  },
  computed: {
    filteredEvents() {
      const list = this.showInactive ? this.events : this.events.filter((e) => e.active);
      return [...list].sort((a, b) => a.eventDate.localeCompare(b.eventDate) || a.code.localeCompare(b.code));
    },
  },
  mounted() {
    this.loadEvents();
  },
  methods: {
    capitalize(str) {
      return str ? str.charAt(0).toUpperCase() + str.slice(1) : str;
    },
    eventTypeColor(eventType) {
      return { dividend: 'success', bonus: 'primary', split: 'warning', buyback: 'secondary' }[eventType] || 'default';
    },
    loadEvents() {
      this.loading = true;
      return fetchWrapper.get('api/gap-backtest/corporate-events').then((res) => {
        this.events = (res?.data || []).map((e) => ({ ...e, key: `${e.code}-${e.eventType}-${e.eventDate}` }));
        this.loading = false;
      }).catch((err) => {
        console.error('Failed to load corporate events:', err);
        this.loading = false;
      });
    },
    // Same endpoint the "Refresh Corporate Events" button on Stock Universe
    // calls -- one real operation (fetch NSE's RSS feed, upsert), two doors
    // into it, so a viewer of this page doesn't have to navigate away just
    // to pull fresh data.
    refreshFromNse() {
      this.refreshing = true;
      this.refreshError = null;
      fetchWrapper.post('api/gap-backtest/refresh-corporate-events').then((res) => {
        this.refreshSummary = res?.data || null;
        this.refreshing = false;
        this.loadEvents();
      }).catch((err) => {
        this.refreshError = typeof err === 'string' ? err : 'Failed to refresh corporate events.';
        this.refreshing = false;
      });
    },
  },
};
</script>
